import React, { useState, useEffect } from 'react';
import '../styles/BookingModal.css';
import logo from '../assets/logo1.png'; // Add this import - adjust path as needed

const BookingModal = ({ isOpen, onClose }) => {
  const [selectedDuration, setSelectedDuration] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('all');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fixed: Auto-open modal after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only open if modal is not already open
      if (!isOpen) {
        // This would need to be handled by the parent component
        // We'll emit an event or call a function passed from parent
        const event = new CustomEvent('openBookingModal');
        window.dispatchEvent(event);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleDurationClick = (hours) => {
    setSelectedDuration(hours);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedDuration) {
      alert('Please select duration');
      return;
    }
    
    const formData = new FormData(e.target);
    
    fetch('https://formspree.io/f/mvzbkdyw', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => {
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        alert('Something went wrong. Please try again.');
      }
    })
    .catch(error => {
      console.error('Error:', error);
      alert('Network error. Please check your connection.');
    });
  };

  const handleClose = () => {
    setIsSubmitted(false);
    setSelectedDuration('');
    setSelectedRegion('all');
    onClose();
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
  };

  // Sports options
  const sportsList = [
    { value: "Football", label: "⚽ Football" },
    { value: "Cricket", label: "🏏 Cricket" },
    { value: "Lawn Tennis", label: "🎾 Lawn Tennis" },
    { value: "Padel Tennis", label: "🎾 Padel Tennis" },
    { value: "Badminton", label: "🏸 Badminton" },
    { value: "Basketball", label: "🏀 Basketball" },
    { value: "Volleyball", label: "🏐 Volleyball" },
    { value: "Pickleball", label: "🥒 Pickleball" }
  ];

  // Location data organized by region
  const locationsByRegion = {
    west: [
      { value: "Al Barsha, Dubai", label: "Al Barsha - West Dubai" },
      { value: "Al Quoz, Dubai", label: "Al Quoz - West Dubai" },
      { value: "Jumeirah 1, Dubai", label: "Jumeirah 1 - West Dubai" },
      { value: "Jumeirah 2, Dubai", label: "Jumeirah 2 - West Dubai" },
      { value: "Jumeirah 3, Dubai", label: "Jumeirah 3 - West Dubai" },
      { value: "Dubai Marina, Dubai", label: "Dubai Marina - West Dubai" },
      { value: "JBR, Dubai", label: "JBR (Jumeirah Beach Residence) - West Dubai" },
      { value: "The Palm Jumeirah, Dubai", label: "The Palm Jumeirah - West Dubai" },
      { value: "Al Sufouh, Dubai", label: "Al Sufouh - West Dubai" },
      { value: "Al Safa, Dubai", label: "Al Safa - West Dubai" },
      { value: "Al Wasl Road, Dubai", label: "Al Wasl Road - West Dubai" },
      { value: "Dubai Media City, Dubai", label: "Dubai Media City - West Dubai" },
      { value: "Dubai Internet City, Dubai", label: "Dubai Internet City - West Dubai" },
      { value: "Barsha Heights, Dubai", label: "Barsha Heights (TECOM) - West Dubai" },
      { value: "Emirates Hills, Dubai", label: "Emirates Hills - West Dubai" },
      { value: "Mina Rashid, Dubai", label: "Mina Rashid - West Dubai" },
      { value: "Al Thanyah, Dubai", label: "Al Thanyah - West Dubai" },
      { value: "The Greens, Dubai", label: "The Greens - West Dubai" },
      { value: "Dubai Sports City, Dubai", label: "Dubai Sports City - West Dubai" },
      { value: "Motor City, Dubai", label: "Motor City - West Dubai" },
      { value: "Arabian Ranches 2, Dubai", label: "Arabian Ranches 2 - West Dubai" },
      { value: "Damac Hills, Dubai", label: "Damac Hills - West Dubai" }
    ],
    east: [
      { value: "Al Rigga, Dubai", label: "Al Rigga - East Dubai" },
      { value: "Al Qusais, Dubai", label: "Al Qusais - East Dubai" },
      { value: "Al Qusais Industrial, Dubai", label: "Al Qusais Industrial - East Dubai" },
      { value: "Deira, Dubai", label: "Deira - East Dubai" },
      { value: "Abu Hail, Dubai", label: "Abu Hail - East Dubai" },
      { value: "Al Nahda, Dubai", label: "Al Nahda - East Dubai" },
      { value: "Muhaisnah, Dubai", label: "Muhaisnah - East Dubai" },
      { value: "Al Twar, Dubai", label: "Al Twar - East Dubai" },
      { value: "Al Twar 1, Dubai", label: "Al Twar 1 - East Dubai" },
      { value: "Al Twar 2, Dubai", label: "Al Twar 2 - East Dubai" },
      { value: "Al Twar 3, Dubai", label: "Al Twar 3 - East Dubai" },
      { value: "Garhoud, Dubai", label: "Garhoud - East Dubai" },
      { value: "Hor Al Anz, Dubai", label: "Hor Al Anz - East Dubai" },
      { value: "Al Baraha, Dubai", label: "Al Baraha - East Dubai" },
      { value: "Al Muteena, Dubai", label: "Al Muteena - East Dubai" },
      { value: "Naif, Dubai", label: "Naif - East Dubai" },
      { value: "Al Ras, Dubai", label: "Al Ras - East Dubai" },
      { value: "Al Buteen, Dubai", label: "Al Buteen - East Dubai" },
      { value: "Al Dhagaya, Dubai", label: "Al Dhagaya - East Dubai" },
      { value: "Ayal Nasir, Dubai", label: "Ayal Nasir - East Dubai" },
      { value: "Mirdif, Dubai", label: "Mirdif - East Dubai" },
      { value: "Mirdif Hills, Dubai", label: "Mirdif Hills - East Dubai" },
      { value: "Umm Ramool, Dubai", label: "Umm Ramool - East Dubai" }
    ],
    central: [
      { value: "Downtown Dubai, Dubai", label: "Downtown Dubai - Central Dubai" },
      { value: "Business Bay, Dubai", label: "Business Bay - Central Dubai" },
      { value: "Dubai Design District, Dubai", label: "Dubai Design District (d3) - Central Dubai" },
      { value: "Al Jaddaf, Dubai", label: "Al Jaddaf - Central Dubai" },
      { value: "Oud Metha, Dubai", label: "Oud Metha - Central Dubai" },
      { value: "Al Mankhool, Dubai", label: "Al Mankhool - Central Dubai" },
      { value: "Al Karama, Dubai", label: "Al Karama - Central Dubai" },
      { value: "Al Hudaiba, Dubai", label: "Al Hudaiba - Central Dubai" },
      { value: "Al Raffa, Dubai", label: "Al Raffa - Central Dubai" },
      { value: "Al Souq Al Kabeer, Dubai", label: "Al Souq Al Kabeer - Central Dubai" },
      { value: "Al Fahidi, Dubai", label: "Al Fahidi - Central Dubai" },
      { value: "Al Hamriya, Dubai", label: "Al Hamriya - Central Dubai" },
      { value: "Port Rashid, Dubai", label: "Port Rashid - Central Dubai" },
      { value: "Zabeel, Dubai", label: "Zabeel - Central Dubai" },
      { value: "Zabeel 1, Dubai", label: "Zabeel 1 - Central Dubai" },
      { value: "Zabeel 2, Dubai", label: "Zabeel 2 - Central Dubai" },
      { value: "Al Kifaf, Dubai", label: "Al Kifaf - Central Dubai" },
      { value: "Al Mina, Dubai", label: "Al Mina - Central Dubai" },
      { value: "Dubai Creek, Dubai", label: "Dubai Creek - Central Dubai" },
      { value: "Al Shindagha, Dubai", label: "Al Shindagha - Central Dubai" }
    ],
    north: [
      { value: "Bur Dubai, Dubai", label: "Bur Dubai - North Dubai" },
      { value: "Al Fahidi, Dubai", label: "Al Fahidi - North Dubai" },
      { value: "Al Sabkha, Dubai", label: "Al Sabkha - North Dubai" },
      { value: "Al Dhagaya, Dubai", label: "Al Dhagaya - North Dubai" },
      { value: "Al Raffa, Dubai", label: "Al Raffa - North Dubai" },
      { value: "Al Mankhool, Dubai", label: "Al Mankhool - North Dubai" },
      { value: "Al Karama, Dubai", label: "Al Karama - North Dubai" },
      { value: "Oud Metha, Dubai", label: "Oud Metha - North Dubai" },
      { value: "Al Hudaiba, Dubai", label: "Al Hudaiba - North Dubai" },
      { value: "Al Souq Al Kabeer, Dubai", label: "Al Souq Al Kabeer - North Dubai" },
      { value: "Al Hamriya, Dubai", label: "Al Hamriya - North Dubai" },
      { value: "Naif, Dubai", label: "Naif - North Dubai" },
      { value: "Al Ras, Dubai", label: "Al Ras - North Dubai" },
      { value: "Al Buteen, Dubai", label: "Al Buteen - North Dubai" },
      { value: "Ayal Nasir, Dubai", label: "Ayal Nasir - North Dubai" },
      { value: "Al Mamzar, Dubai", label: "Al Mamzar - North Dubai" },
      { value: "Deira Islands, Dubai", label: "Deira Islands - North Dubai" }
    ],
    south: [
      { value: "Dubai Silicon Oasis, Dubai", label: "Dubai Silicon Oasis (DSO) - South Dubai" },
      { value: "Arabian Ranches, Dubai", label: "Arabian Ranches - South Dubai" },
      { value: "Arabian Ranches 1, Dubai", label: "Arabian Ranches 1 - South Dubai" },
      { value: "Dubai Investments Park, Dubai", label: "Dubai Investments Park (DIP) - South Dubai" },
      { value: "Jumeirah Golf Estates, Dubai", label: "Jumeirah Golf Estates - South Dubai" },
      { value: "Al Furjan, Dubai", label: "Al Furjan - South Dubai" },
      { value: "Dubai Production City, Dubai", label: "Dubai Production City (IMPZ) - South Dubai" },
      { value: "Dubai South, Dubai", label: "Dubai South - South Dubai" },
      { value: "Al Maktoum International Airport, Dubai", label: "Al Maktoum International Airport - South Dubai" },
      { value: "Dubai World Central, Dubai", label: "Dubai World Central (DWC) - South Dubai" },
      { value: "Expo City Dubai, Dubai", label: "Expo City Dubai - South Dubai" },
      { value: "The Sustainable City, Dubai", label: "The Sustainable City - South Dubai" },
      { value: "Town Square, Dubai", label: "Town Square - South Dubai" },
      { value: "Mudon, Dubai", label: "Mudon - South Dubai" },
      { value: "Al Salam, Dubai", label: "Al Salam - South Dubai" },
      { value: "Al Yalayis, Dubai", label: "Al Yalayis - South Dubai" },
      { value: "Discovery Gardens, Dubai", label: "Discovery Gardens - South Dubai" },
      { value: "Al Furjan, Dubai", label: "Al Furjan - South Dubai" }
    ]
  };

  // Combine all locations for "All Regions" option
  const allLocations = [
    ...locationsByRegion.west,
    ...locationsByRegion.east,
    ...locationsByRegion.central,
    ...locationsByRegion.north,
    ...locationsByRegion.south
  ];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay active" onClick={handleClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <button className="close-modal" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
          <div className="modal-logo">
            <img src={logo} alt="BookMyGround" className="modal-logo-img" />
          </div>
          <div className="offer-badge">
            <i className="fas fa-tag"></i> FLAT 20% OFF
          </div>
        </div>
        
        <div className="modal-body" style={{ display: isSubmitted ? 'none' : 'block' }}>
          <form id="bookingForm" onSubmit={handleSubmit}>
            <input type="hidden" name="_subject" value="New Booking Request - BookMyGround" />
            
            <div className="form-group">
              <label><i className="fas fa-user"></i> Your Name</label>
              <input type="text" name="name" className="form-control" placeholder="Enter your full name" required />
            </div>
            
            <div className="form-group">
              <label><i className="fas fa-phone-alt"></i> Mobile Number</label>
              <input type="tel" name="phone" className="form-control" placeholder="+971 50 123 4567" required />
            </div>
            
            <div className="form-group">
              <label><i className="fas fa-envelope"></i> Email Address</label>
              <input type="email" name="email" className="form-control" placeholder="your@email.com" required />
            </div>
            
            {/* Sport Selection Dropdown */}
            <div className="form-group">
              <label><i className="fas fa-futbol"></i> Select Sports</label>
              <select name="sport" className="form-control" required>
                <option value="" disabled selected>Choose a sport</option>
                {sportsList.map(sport => (
                  <option key={sport.value} value={sport.value}>{sport.label}</option>
                ))}
              </select>
            </div>
            
            {/* Region Filter for Ground Selection */}
            <div className="form-group">
              <label><i className="fas fa-globe"></i> Filter by Region</label>
              <div className="region-filter-buttons">
                <button 
                  type="button"
                  className={`region-filter-btn ${selectedRegion === 'all' ? 'active' : ''}`}
                  onClick={() => handleRegionChange('all')}
                >
                  All Regions
                </button>
                <button 
                  type="button"
                  className={`region-filter-btn ${selectedRegion === 'west' ? 'active' : ''}`}
                  onClick={() => handleRegionChange('west')}
                >
                  West Dubai
                </button>
                <button 
                  type="button"
                  className={`region-filter-btn ${selectedRegion === 'east' ? 'active' : ''}`}
                  onClick={() => handleRegionChange('east')}
                >
                  East Dubai
                </button>
                <button 
                  type="button"
                  className={`region-filter-btn ${selectedRegion === 'central' ? 'active' : ''}`}
                  onClick={() => handleRegionChange('central')}
                >
                  Central Dubai
                </button>
                <button 
                  type="button"
                  className={`region-filter-btn ${selectedRegion === 'north' ? 'active' : ''}`}
                  onClick={() => handleRegionChange('north')}
                >
                  North Dubai
                </button>
                <button 
                  type="button"
                  className={`region-filter-btn ${selectedRegion === 'south' ? 'active' : ''}`}
                  onClick={() => handleRegionChange('south')}
                >
                  South Dubai
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label><i className="fas fa-map-marker-alt"></i> Select Ground Location</label>
              <select name="location" className="form-control" required>
                <option value="" disabled selected>Choose a ground location</option>
                
                {selectedRegion === 'all' && (
                  <>
                    <optgroup label="📍 WEST DUBAI">
                      {locationsByRegion.west.map(loc => (
                        <option key={loc.value} value={loc.value}>{loc.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="📍 EAST DUBAI">
                      {locationsByRegion.east.map(loc => (
                        <option key={loc.value} value={loc.value}>{loc.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="📍 CENTRAL DUBAI">
                      {locationsByRegion.central.map(loc => (
                        <option key={loc.value} value={loc.value}>{loc.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="📍 NORTH DUBAI">
                      {locationsByRegion.north.map(loc => (
                        <option key={loc.value} value={loc.value}>{loc.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="📍 SOUTH DUBAI">
                      {locationsByRegion.south.map(loc => (
                        <option key={loc.value} value={loc.value}>{loc.label}</option>
                      ))}
                    </optgroup>
                  </>
                )}
                
                {selectedRegion === 'west' && (
                  <optgroup label="📍 WEST DUBAI">
                    {locationsByRegion.west.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </optgroup>
                )}
                
                {selectedRegion === 'east' && (
                  <optgroup label="📍 EAST DUBAI">
                    {locationsByRegion.east.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </optgroup>
                )}
                
                {selectedRegion === 'central' && (
                  <optgroup label="📍 CENTRAL DUBAI">
                    {locationsByRegion.central.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </optgroup>
                )}
                
                {selectedRegion === 'north' && (
                  <optgroup label="📍 NORTH DUBAI">
                    {locationsByRegion.north.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </optgroup>
                )}
                
                {selectedRegion === 'south' && (
                  <optgroup label="📍 SOUTH DUBAI">
                    {locationsByRegion.south.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              <small className="location-count">{selectedRegion === 'all' ? allLocations.length : locationsByRegion[selectedRegion].length} locations available</small>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label><i className="fas fa-calendar-alt"></i> Date</label>
                <input type="date" name="date" className="form-control" required />
              </div>
              <div className="form-group">
                <label><i className="fas fa-clock"></i> Time</label>
                <input type="time" name="time" className="form-control" required />
              </div>
            </div>
            
            <div className="form-group">
              <label><i className="fas fa-hourglass-half"></i> Duration</label>
              <div className="duration-selector">
                {[1,2,3,4,5,6].map(hours => (
                  <span
                    key={hours}
                    className={`duration-option ${selectedDuration === hours.toString() ? 'selected' : ''}`}
                    onClick={() => handleDurationClick(hours.toString())}
                  >
                    {hours}hr
                  </span>
                ))}
              </div>
              <input type="hidden" name="duration" value={selectedDuration} />
            </div>
            
            <div className="form-group">
              <label><i className="fas fa-comment"></i> Special Requests (Optional)</label>
              <textarea name="message" className="form-control" rows="2" placeholder="Any special requirements..."></textarea>
            </div>
            
            <button type="submit" className="submit-btn">
              <i className="fas fa-calendar-check"></i> BOOK NOW
            </button>
          </form>
        </div>
        
        <div className={`modal-body success-message ${isSubmitted ? 'show' : ''}`}>
          <div className="success-icon">
            <i className="fas fa-check"></i>
          </div>
          <h3>Booking Confirmed!</h3>
          <p>Your booking request has been received.</p>
          <p style={{ fontWeight: '600', color: '#0F2B43' }}>We will contact you within 30 minutes</p>
          <div className="confirmation-note">
            <i className="fas fa-phone-alt" style={{ marginRight: '8px' }}></i>
            for final confirmation
          </div>
          <button className="close-success" onClick={handleClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;